import { useState, useEffect } from 'react'
import { Check, Loader2, Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getProfile, updateProfile } from '../api'
import type { ProfileData } from '../api'

export default function ProfileExpanded() {
  const { t } = useTranslation('settings')
  const { t: tc } = useTranslation('common')
  const [profile, setProfile] = useState<ProfileData | null>(null)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [location, setLocation] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    getProfile()
      .then((data) => {
        setProfile(data)
        setFirstName(data.firstName ?? '')
        setLastName(data.lastName ?? '')
        setLocation(data.location ?? '')
      })
      .catch(() => setError(t('profile.failedToLoad')))
      .finally(() => setLoading(false))
  }, [t])

  const isDirty =
    profile !== null &&
    (firstName.trim() !== (profile.firstName ?? '') ||
      lastName.trim() !== (profile.lastName ?? '') ||
      location.trim() !== (profile.location ?? ''))

  const handleSave = async () => {
    if (!isDirty) return
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const updated = await updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        location: location.trim(),
      })
      setProfile(updated)
      setFirstName(updated.firstName ?? '')
      setLastName(updated.lastName ?? '')
      setLocation(updated.location ?? '')
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('profile.failedToSave'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 size={18} className="animate-spin text-pearl/60" />
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {error && <p className="text-xs text-rose/70">{error}</p>}

      {/* First Name */}
      <div className="space-y-1">
        <label className="text-xs text-pearl/50">{t('profile.firstName')}</label>
        <input
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave() }}
          placeholder={t('profile.firstNamePlaceholder')}
          disabled={saving}
          className="w-full bg-slate-mid/30 rounded-lg px-3 py-1.5 text-sm text-ivory placeholder:text-pearl/55 outline-none focus:ring-1 focus:ring-gold/40 disabled:opacity-50"
        />
      </div>

      {/* Last Name */}
      <div className="space-y-1">
        <label className="text-xs text-pearl/50">{t('profile.lastName')}</label>
        <input
          type="text"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave() }}
          placeholder={t('profile.lastNamePlaceholder')}
          disabled={saving}
          className="w-full bg-slate-mid/30 rounded-lg px-3 py-1.5 text-sm text-ivory placeholder:text-pearl/55 outline-none focus:ring-1 focus:ring-gold/40 disabled:opacity-50"
        />
      </div>

      {/* Location */}
      <div className="space-y-1">
        <label className="text-xs text-pearl/50">{t('profile.location')}</label>
        <input
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave() }}
          placeholder={t('profile.locationPlaceholder')}
          disabled={saving}
          className="w-full bg-slate-mid/30 rounded-lg px-3 py-1.5 text-sm text-ivory placeholder:text-pearl/55 outline-none focus:ring-1 focus:ring-gold/40 disabled:opacity-50"
        />
      </div>

      {/* Email (read-only) */}
      {profile?.email && (
        <div className="space-y-1">
          <label className="text-xs text-pearl/50">{t('profile.email')}</label>
          <div className="flex items-center gap-2 px-3 py-1.5">
            <span className="text-sm text-pearl/60">{profile.email}</span>
            <Lock size={12} className="text-pearl/55" />
          </div>
        </div>
      )}

      {/* Save button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          handleSave()
        }}
        disabled={!isDirty || saving}
        className="w-full py-2 rounded-full bg-gold/10 text-gold text-sm font-medium hover:bg-gold/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {saving ? (
          <><Loader2 size={14} className="animate-spin" /> {tc('actions.saving')}</>
        ) : saved ? (
          <><Check size={14} /> {tc('actions.saved')}</>
        ) : (
          tc('actions.save')
        )}
      </button>
    </div>
  )
}
